import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Plane, ExternalLink } from 'lucide-react';
import { API } from '../config';

/**
 * Native ad card — visually matches the grid it's placed in.
 *
 * variant: "route" | "blog" | "airline" | "discover" | "sidebar"
 *
 * The card is designed to be invisible in the grid flow on mobile
 * while clearly labelled "Sponsored" so it's not deceptive.
 */
export default function NativeAdCard({ ad, variant = 'route' }) {
  const navigate = useNavigate();

  const handleClick = async () => {
    try { await axios.post(`${API}/ads/${ad.ad_id}/click`); } catch {}
    const url = ad.cta_url || '/contact';
    if (url.startsWith('/') || url.startsWith(window.location.origin)) {
      navigate(url.replace(window.location.origin, ''));
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const SponsoredBadge = ({ dark = false }) => (
    <span className={`text-[9px] font-semibold tracking-widest uppercase px-1.5 py-0.5 rounded
      ${dark ? 'bg-black/50 text-white/80' : 'bg-[#FF4500]/10 text-[#FF4500]/80 border border-[#FF4500]/20'}`}>
      {ad.badge || 'Sponsored'}
    </span>
  );

  // ── Route variant ────────────────────────────────────────────────────────
  if (variant === 'route') {
    return (
      <button onClick={handleClick} className="w-full group text-left focus:outline-none" data-testid="native-ad-route">
        <Card className="fly-card overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer border-[#FF4500]/20">
          <div className="relative h-24 overflow-hidden">
            {ad.image_url ? (
              <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#FF4500]/30 to-[#1E3A8A]/40" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute top-2 left-2"><SponsoredBadge dark /></div>
            <div className="absolute bottom-2 left-3 right-3">
              <p className="text-white text-sm font-bold drop-shadow line-clamp-1" style={{ fontFamily: 'Space Grotesk' }}>{ad.title}</p>
            </div>
          </div>
          <div className="p-3">
            <p className="text-xs text-muted-foreground line-clamp-2 mb-2 leading-relaxed">{ad.description}</p>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#FF4500] group-hover:underline">
              {ad.cta_text || 'Learn More'} <ExternalLink className="w-3 h-3" />
            </span>
          </div>
        </Card>
      </button>
    );
  }

  // ── Blog variant ─────────────────────────────────────────────────────────
  if (variant === 'blog') {
    return (
      <button onClick={handleClick} className="w-full group text-left focus:outline-none" data-testid="native-ad-blog">
        <Card className="fly-card overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer h-full border-[#FF4500]/20">
          {ad.image_url ? (
            <div className="h-40 overflow-hidden">
              <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            </div>
          ) : (
            <div className="h-40 bg-gradient-to-br from-[#FF4500]/20 to-[#1E3A8A]/30" />
          )}
          <div className="p-4">
            <div className="mb-2"><SponsoredBadge /></div>
            <h3 className="text-sm font-semibold leading-tight mb-2 line-clamp-2" style={{ fontFamily: 'Space Grotesk' }}>{ad.title}</h3>
            <p className="text-xs text-muted-foreground line-clamp-2 mb-3 leading-relaxed">{ad.description}</p>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#FF4500] group-hover:underline">
              {ad.cta_text || 'Learn More'} <ExternalLink className="w-3 h-3" />
            </span>
          </div>
        </Card>
      </button>
    );
  }

  // ── Airline variant ───────────────────────────────────────────────────────
  if (variant === 'airline') {
    return (
      <button onClick={handleClick} className="w-full group text-left focus:outline-none" data-testid="native-ad-airline">
        <Card className="fly-card p-3.5 hover:shadow-lg transition-all h-full cursor-pointer border-[#FF4500]/20">
          <div className="flex items-start gap-2.5 mb-2">
            {/* Branded logo area */}
            <div className="w-10 h-10 rounded-xl bg-[#FF4500]/10 flex items-center justify-center flex-shrink-0 border border-[#FF4500]/20">
              {ad.image_url
                ? <img src={ad.image_url} alt="" className="w-8 h-8 object-contain rounded-lg" />
                : <Plane className="w-5 h-5 text-[#FF4500]" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight truncate group-hover:text-[#FF4500] transition-colors">{ad.title}</p>
              <div className="mt-0.5"><SponsoredBadge /></div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed mb-2">{ad.description}</p>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#FF4500] group-hover:underline">
            {ad.cta_text || 'Learn More'} <ExternalLink className="w-3 h-3" />
          </span>
        </Card>
      </button>
    );
  }

  // ── Discover / Airport grid variant ──────────────────────────────────────
  if (variant === 'discover') {
    return (
      <button onClick={handleClick} className="w-full group text-left focus:outline-none" data-testid="native-ad-discover">
        <Card className="fly-card overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer h-full border-[#FF4500]/20">
          <div className="h-36 overflow-hidden relative">
            {ad.image_url ? (
              <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#FF4500]/20 to-[#1E3A8A]/30 flex items-center justify-center">
                <Plane className="w-10 h-10 text-[#FF4500]/40" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute top-2 right-2"><SponsoredBadge dark /></div>
          </div>
          <div className="p-3">
            <p className="text-sm font-semibold truncate mb-1" style={{ fontFamily: 'Space Grotesk' }}>{ad.title}</p>
            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{ad.description}</p>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#FF4500] group-hover:underline">
              {ad.cta_text || 'Learn More'} <ExternalLink className="w-3 h-3" />
            </span>
          </div>
        </Card>
      </button>
    );
  }

  // ── Sidebar variant (airport / discover desktop) ──────────────────────────
  return (
    <button onClick={handleClick} className="w-full group text-left focus:outline-none" data-testid="native-ad-sidebar">
      <Card className="fly-card overflow-hidden border-2 border-dashed border-[#FF4500]/25 hover:border-[#FF4500]/50 transition-all">
        {ad.image_url ? (
          <div className="h-40 overflow-hidden">
            <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          </div>
        ) : (
          <div className="h-40 bg-gradient-to-br from-[#FF4500]/10 to-[#1E3A8A]/15 flex items-center justify-center">
            <Plane className="w-12 h-12 text-[#FF4500]/30" />
          </div>
        )}
        <div className="p-4 space-y-3">
          <SponsoredBadge />
          <p className="text-sm font-bold leading-snug" style={{ fontFamily: 'Space Grotesk' }}>{ad.title}</p>
          <p className="text-xs text-muted-foreground leading-relaxed">{ad.description}</p>
          <div className="w-full py-2 rounded-full bg-[#FF4500] text-white text-xs font-semibold text-center group-hover:bg-[#E03E00] transition-colors">
            {ad.cta_text || 'Learn More'}
          </div>
        </div>
      </Card>
      <p className="text-[10px] text-muted-foreground/40 text-center mt-1">Advertisement</p>
    </button>
  );
}
